import { useCallback, useMemo, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue';
import type { PageParams } from '@/types/api';

export interface TableQueryState {
  /** Ready to pass straight to an endpoint as query parameters. */
  params: PageParams;
  page: number;
  size: number;
  sort: string | undefined;
  searchInput: string;
  setSearchInput: (value: string) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  /** Toggles ascending/descending when the same column is chosen again. */
  toggleSort: (property: string) => void;
}

/**
 * Pagination, sorting and search state for a list screen.
 *
 * Shared so that eleven list screens do not each reimplement it — and, more
 * importantly, do not each get the reset-to-first-page rule subtly different.
 */
export function useTableQuery(initialSort?: string, initialSize = 20): TableQueryState {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(initialSize);
  const [sort, setSort] = useState<string | undefined>(initialSort);
  const [searchInput, setSearchInput] = useState('');

  const search = useDebouncedValue(searchInput, 300);

  /*
   * Narrowing a search while on page 4 would otherwise ask for a page that no
   * longer exists and render an empty table, which reads as "no results" when
   * there are plenty on page 1.
   */
  const changeSearch = useCallback((value: string) => {
    setSearchInput(value);
    setPage(0);
  }, []);

  const toggleSort = useCallback((property: string) => {
    setSort((current) => {
      const [currentProperty, direction] = (current ?? '').split(',');
      if (currentProperty !== property) {
        return `${property},asc`;
      }
      return direction === 'asc' ? `${property},desc` : `${property},asc`;
    });
    setPage(0);
  }, []);

  const changeSize = useCallback((value: number) => {
    setSize(value);
    setPage(0);
  }, []);

  const params = useMemo<PageParams>(
    () => ({
      page,
      size,
      ...(sort !== undefined ? { sort } : {}),
      ...(search.trim() !== '' ? { search: search.trim() } : {}),
    }),
    [page, size, sort, search],
  );

  return {
    params,
    page,
    size,
    sort,
    searchInput,
    setSearchInput: changeSearch,
    setPage,
    setSize: changeSize,
    toggleSort,
  };
}
